import { memo } from 'react';
import { motion } from 'framer-motion';
import { Workflow, Code, Check } from 'lucide-react';
import { Button, SectionHeader } from '@/src/components/ui';

const tiers = [
  {
    id: 'standard',
    title: 'Automatización Estándar',
    tech: 'POWERED BY n8n',
    techColor: 'blue',
    description:
      'Ideal para conectar apps, mover datos entre CRMs y automatizar tareas repetitivas sin lógica compleja. Rápido y eficiente.',
    features: [
      'Flujos de trabajo visuales',
      'Integración de +200 Apps',
      'Desarrollo Low-Code rápido',
      'Mantenimiento simplificado',
    ],
    icon: <Workflow size={24} />,
    highlighted: false,
  },
  {
    id: 'enterprise',
    title: 'Agentes Autónomos',
    tech: 'POWERED BY CrewAI & Python',
    techColor: 'indigo',
    description:
      'Sistemas multi-agente que "piensan", planean y ejecutan tareas complejas. Código a medida para problemas empresariales únicos.',
    features: [
      'Lógica de razonamiento avanzada',
      'Arquitectura Modular 100% Custom',
      'Memoria y Contexto Persistente',
      'Ejecución autónoma de tareas complejas',
    ],
    icon: <Code size={24} />,
    highlighted: true,
  },
];

function Pricing() {
  return (
    <section id="precios" className="container mx-auto px-6 py-24 relative z-20">
      <SectionHeader
        badge="Modelos de Servicio"
        title="Escalabilidad a tu medida"
        subtitle="Adaptamos la tecnología a la complejidad de tu problema. Desde flujos visuales rápidos hasta enjambres de agentes autónomos programados a medida."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {tiers.map((tier, idx) => (
          <motion.div
            key={tier.id}
            initial={{ opacity: 0, x: idx === 0 ? -20 : 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            className={`p-8 rounded-3xl relative group transition-all duration-300 ${
              tier.highlighted
                ? 'bg-[#080808] border border-indigo-500/30 shadow-[0_0_50px_rgba(79,70,229,0.1)]'
                : 'bg-[#0a0a0a] border border-white/10 hover:border-blue-500/30'
            }`}
          >
            {/* Background glow */}
            {tier.highlighted ? (
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-3xl pointer-events-none" />
            ) : (
              <div className="absolute inset-0 bg-blue-900/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-3xl pointer-events-none" />
            )}

            {/* Premium badge */}
            {tier.highlighted && (
              <div className="absolute top-0 right-0 p-4">
                <span className="bg-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-lg shadow-indigo-500/50">
                  Premium Tech
                </span>
              </div>
            )}

            <div className="flex items-center gap-3 mb-6 relative z-10">
              <div
                className={`p-3 rounded-xl border ${
                  tier.highlighted
                    ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                    : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                }`}
              >
                {tier.icon}
              </div>
              <div>
                <h4 className="text-xl font-bold text-white">{tier.title}</h4>
                <div className="flex items-center gap-2 text-xs text-gray-500 font-mono mt-1">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      tier.highlighted ? 'bg-indigo-500 animate-pulse' : 'bg-blue-500'
                    }`}
                  />
                  {tier.tech}
                </div>
              </div>
            </div>

            <p className="text-gray-400 mb-8 min-h-[60px] relative z-10">{tier.description}</p>

            <ul className="space-y-4 mb-8 relative z-10">
              {tier.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-gray-300">
                  <Check
                    size={16}
                    className={tier.highlighted ? 'text-indigo-500' : 'text-blue-500'}
                  />
                  {feature}
                </li>
              ))}
            </ul>

            <Button
              variant={tier.highlighted ? 'secondary' : 'outline'}
              className={`w-full ${tier.highlighted ? 'bg-indigo-600 hover:bg-indigo-500' : ''}`}
              onClick={() => window.open('https://calendly.com/intelligroow-nvmb/30min', '_blank')}
            >
              {tier.highlighted ? 'Hablar con un Experto' : 'Consultar Precios'}
            </Button>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export default memo(Pricing);
